import { useEffect, useRef, useState } from 'react';
import { Container, Box, CircularProgress, Stack } from '@mui/material';
import {
    useGetUserInfoQuery,
    useGetCategoriesResourcesQuery,
    useGetPlanResourcesQuery,
    useRecordMetricsEventMutation,
} from '@/store/apiSlice';
import { useAppSelector } from '@/store/hooks';
import Event from '@/components/Event';
import UserModal from '@/components/modals/UserModal';
import MailboxModal from '@/components/modals/MailboxModal';
import FilterModal from '@/components/modals/FilterModal';
import EventModal from '@/components/modals/EventModal';
import AbsentModal from '@/components/modals/AbsentModal';
import AgendaModal from '@/components/modals/AgendaModal';
import FilesModal from '@/components/modals/FilesModal';
import ScheduleModal from '@/components/modals/ScheduleModal';
import CategoryRequestModal from '@/components/modals/CategoryRequestModal';
import CategoryRequestsReviewModal from '@/components/modals/CategoryRequestsReviewModal';
import HandbookModal from '@/components/modals/HandbookModal';
import ScheduleTemplatesModal from '@/components/modals/ScheduleTemplatesModal.tsx';
import ChangeRequestsModal from '@/components/modals/ChangeRequestsModal';
import YearSummaryModal from '@/components/modals/YearSummaryModal';
import PersonalForecastModal from '@/components/modals/PersonalForecastModal';
import CustomAppBar from '@/components/AppBar';
import Menu from '@/components/Menu.tsx';
import WelcomeDialog from '@/components/dialogs/WelcomeDialog';
import { isWelcomeDialogDismissed } from '@/utils/helpers.ts';
import { useI18nContext } from '@/i18n/i18n-react';
import { useModalNavigation } from '@/hooks/useModalNavigation';

const MainPage = () => {
    const { LL } = useI18nContext();
    useModalNavigation();
    const { history } = useAppSelector((state) => state.ui.open);
    const forecast = useAppSelector((state) => state.ui.forecast);

    const [currentHash, setCurrentHash] = useState(window.location.hash);
    const [welcomeOpen, setWelcomeOpen] = useState(false);

    const { data: userInfo, isLoading: userLoading } = useGetUserInfoQuery();

    const { isLoading: categoryResourcesLoading } = useGetCategoriesResourcesQuery();
    const { data: planData, isLoading: planLoading } = useGetPlanResourcesQuery(
        { forecast, includeHistory: history },
        { skip: !userInfo }
    );

    const [recordMetricsEvent] = useRecordMetricsEventMutation();
    // Track the last key we fired so we only send once per unique (forecast, history, userId) combination
    const lastFiredPlanViewKey = useRef<string | null>(null);
    useEffect(() => {
        if (!planData || !userInfo) return;
        const withHistory = !!history;
        const key = `${userInfo.userId}|${forecast}|${withHistory}`;
        if (lastFiredPlanViewKey.current === key) return;
        lastFiredPlanViewKey.current = key;
        recordMetricsEvent({
            eventType: 'plan_viewed',
            meta: { forecast, withHistory },
        });
    }, [planData, userInfo, forecast, history]);

    // @ts-ignore
    //globalThis['welcome'] = () => setWelcomeOpen(true); // ToDo remove

    // Show welcome dialog when user has no assignments and hasn't dismissed
    useEffect(() => {
        try {
            if (userInfo && Array.isArray(userInfo.categoriesAssigned)) {
                if (userInfo.categoriesAssigned.length === 0 && !isWelcomeDialogDismissed()) {
                    setWelcomeOpen(true);
                }
            }
        } catch (e) {
            // ignore
        }
    }, [userInfo]);

    useEffect(() => {
        document.title = `${location.host} - ${LL.mainPage.title()}`;
    }, [location.host, LL]);

    // Listen for hash changes
    useEffect(() => {
        const handleHashChange = () => {
            setCurrentHash(window.location.hash);

            // Scroll to the event if hash matches
            if (window.location.hash && window.location.hash.startsWith('#d')) {
                const element = document.getElementById(window.location.hash.substring(1));
                if (element) {
                    // Small delay to ensure render is complete
                    setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                }
            }
        };

        window.addEventListener('hashchange', handleHashChange);

        // Also handle initial hash on mount
        handleHashChange();

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    if (userLoading || categoryResourcesLoading) {
        return (
            <Stack
                direction="row"
                sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
            >
                <CircularProgress />
            </Stack>
        );
    }

    if (!userInfo) {
        return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toDateString();

    return (
        <Box>
            <CustomAppBar variant="main" />
            <Menu isAdmin={userInfo.admin} />

            <WelcomeDialog open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />

            <UserModal />
            <MailboxModal />
            <FilterModal />
            <EventModal />
            <AbsentModal />
            <AgendaModal />
            <ChangeRequestsModal />
            <FilesModal />
            <ScheduleModal />
            <ScheduleTemplatesModal />
            <YearSummaryModal />
            <PersonalForecastModal />
            <CategoryRequestModal />
            <CategoryRequestsReviewModal />
            <HandbookModal />

            <Container maxWidth={false} sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                {planLoading ? (
                    <Stack
                        direction="row"
                        sx={{ alignItems: 'center', justifyContent: 'center', py: 4 }}
                    >
                        <CircularProgress />
                    </Stack>
                ) : (
                    (() => {
                        if (!planData || planData.length === 0) {
                            return null;
                        }

                        // Check if there's a hash in the URL (e.g., #d20251130)
                        let highlightedDate: string | undefined;

                        if (currentHash && currentHash.startsWith('#d')) {
                            // Extract date from hash (format: #d20251130)
                            const hashDate = currentHash.substring(2); // Remove '#d'
                            // Convert to YYYY-MM-DD format
                            const year = hashDate.substring(0, 4);
                            const month = hashDate.substring(4, 6);
                            const day = hashDate.substring(6, 8);
                            const formattedDate = `${year}-${month}-${day}`;

                            // Check if this date exists in our plan data
                            const hashEvent = planData.find(
                                (event) => event.date === formattedDate
                            );
                            if (hashEvent) {
                                highlightedDate = hashEvent.date;
                            }
                        }

                        // If no hash match, use default logic
                        if (!highlightedDate) {
                            const todayEvent = planData.find(
                                (event) => new Date(event.date).toDateString() === todayStr
                            );

                            highlightedDate = todayEvent
                                ? todayEvent.date
                                : planData.find((event) => new Date(event.date) >= today)?.date ||
                                  planData[planData.length - 1].date;
                        }

                        return planData.map((row) => (
                            <Event
                                key={row.date}
                                data={row}
                                isHighlighted={row.date === highlightedDate}
                                autoCollapse={forecast > 20}
                            />
                        ));
                    })()
                )}
            </Container>
        </Box>
    );
};

export default MainPage;
